import React, { useState, useEffect } from 'react';
import { useJazyk } from '../context/LanguageContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { getAvatarImage } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { MapPin, Users, Lock, Globe, ChevronRight, Save } from 'lucide-react';

import { API } from '../lib/api';
import { STAGES, nazevFaze } from '../lib/journeyStages';

export default function JourneyPage() {
  const { t } = useJazyk();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStage, setCurrentStage] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [similarUsers, setSimilarUsers] = useState([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    // Load current user's journey from their profile
    const fetchMyJourney = async () => {
      try {
        const r = await axios.get(`${API}/users/me`);
        const journey = r.data?.journey;
        if (journey) {
          setCurrentStage(journey.stage || '');
          setIsPublic(journey.is_public || false);
          setNote(journey.note || '');
        }
      } catch {}
    };
    fetchMyJourney();
  }, []);

  const handleSave = async () => {
    if (!currentStage) { toast.error(t('cesta.vyberteFazi')); return; }
    setSaving(true);
    const stageData = STAGES.find(s => s.id === currentStage);
    try {
      await axios.put(`${API}/users/me/journey`, {
        stage: currentStage,
        stage_label: stageData?.label || currentStage,
        is_public: isPublic,
        note
      });
      toast.success(t('cesta.ulozeno'));
      setSaved(true);
      if (isPublic) fetchSimilar();
    } catch { toast.error(t('cesta.chyba')); }
    finally { setSaving(false); }
  };

  const fetchSimilar = async () => {
    setLoadingSimilar(true);
    try {
      const r = await axios.get(`${API}/journey/similar`);
      setSimilarUsers(r.data);
    } catch {} finally { setLoadingSimilar(false); }
  };

  const selectedStage = STAGES.find(s => s.id === currentStage);

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
      {/* Header */}
      <div className="space-y-2">
        <div className="h-1 w-16 rounded bg-gradient-to-r from-[#5BCEFA] to-[#F5A9B8]" />
        <h1 className="font-serif text-2xl sm:text-3xl font-bold text-bloom-text">{t('nav.mojeCesta')}</h1>
        <p className="text-bloom-sub text-sm leading-relaxed max-w-lg">
              {t('cesta.volitelne')}
            </p>
      </div>

      {/* Roadmap */}
      <Card className="bg-white border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-bloom-text">{t('cesta.kdeJsem')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {STAGES.map(stage => (
              <button
                key={stage.id}
                onClick={() => setCurrentStage(stage.id)}
                className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-all hover:shadow-sm ${
                  currentStage === stage.id
                    ? 'border-bloom-violet/60 shadow-sm bg-bloom-violet/5'
                    : 'border-border/50 bg-white hover:border-bloom-violet/30'
                }`}
                data-testid={`stage-${stage.id}`}
              >
                <div
                  className="w-4 h-4 rounded-full shrink-0 ring-2 ring-white shadow-sm"
                  style={{
                    background: stage.color,
                    boxShadow: currentStage === stage.id ? `0 0 0 3px ${stage.color}50` : undefined
                  }}
                />
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-semibold truncate ${currentStage === stage.id ? 'text-bloom-violet' : 'text-bloom-text'}`}>
                    {t(stage.klic)}
                  </p>
                  {currentStage === stage.id && (
                    <p className="text-[10px] text-bloom-sub mt-0.5 leading-relaxed line-clamp-2">{t(stage.klicPopis)}</p>
                  )}
                </div>
                <Link to={`/journey/${stage.id}`} onClick={e => e.stopPropagation()}
                  className="text-[10px] font-medium text-bloom-violet hover:underline shrink-0 whitespace-nowrap"
                  data-testid={`stage-guide-link-${stage.id}`}>
                  {t('cesta.coMeCeka')}
                </Link>
                {currentStage === stage.id && <ChevronRight className="w-3.5 h-3.5 text-bloom-violet shrink-0" />}
              </button>
            ))}
          </div>

          {/* Color legend */}
          <div className="mt-4 pt-3 border-t border-border/40">
            <p className="text-[10px] text-bloom-sub/60 mb-2 font-medium uppercase tracking-wide">{t('cesta.legendaBarev')}</p>
            <div className="flex flex-wrap gap-2">
              {STAGES.map(s => (
                <div key={s.id} className="flex items-center gap-1">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
                  <span className="text-[10px] text-bloom-sub/70">{nazevFaze(t, s.id, s.label).split(' ').slice(0, 2).join(' ')}</span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Note */}
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-bloom-text">{t('cesta.poznamka')} <span className="text-bloom-sub font-normal">{t('cesta.volitelna')}</span></label>
        <Textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder={t('cesta.poznamkaFazePriklad')}
          rows={3}
          className="text-sm"
          data-testid="journey-note-input"
        />
      </div>

      {/* Privacy toggle */}
      <Card className="bg-white border-border/50">
        <CardContent className="p-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {isPublic ? <Globe className="w-4 h-4 text-bloom-violet" /> : <Lock className="w-4 h-4 text-bloom-sub" />}
            <div>
              <p className="text-sm font-medium text-bloom-text">
                {isPublic ? t('cesta.viditelna') : t('cesta.soukroma')}
              </p>
              <p className="text-xs text-bloom-sub">
                {isPublic ? t('cesta.mohouNajit') : t('cesta.jenTy')}
              </p>
            </div>
          </div>
          <Switch
            checked={isPublic}
            onCheckedChange={setIsPublic}
            data-testid="journey-public-toggle"
          />
        </CardContent>
      </Card>

      {/* Save */}
      <Button
        onClick={handleSave}
        className="w-full bg-bloom-violet text-white"
        disabled={saving || !currentStage}
        data-testid="save-journey-btn"
      >
        <Save className="w-4 h-4 mr-2" />
        {saving ? t('pf.ukladam') : t('cesta.ulozit')}
      </Button>

      {/* Find similar */}
      {currentStage && (
        <Card className="bg-gradient-to-br from-bloom-violet/5 to-[#5BCEFA]/5 border-bloom-violet/20">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-bloom-text flex items-center gap-2">
                <Users className="w-4 h-4 text-bloom-violet" />{t('cesta.najitPodobne')}
              </p>
              <Button
                size="sm"
                variant="outline"
                className="border-bloom-violet/30 text-bloom-violet"
                onClick={fetchSimilar}
                disabled={loadingSimilar}
                data-testid="find-similar-btn"
              >
                {loadingSimilar ? t('cesta.hledam') : 'Hledat'}
              </Button>
            </div>
            {selectedStage && (
              <p className="text-xs text-bloom-sub">
                {t('cesta.hledatVeFazi')} <strong>{t(selectedStage.klic)}</strong> {t('cesta.jenVerejne')}
              </p>
            )}
            {similarUsers.length > 0 && (
              <div className="space-y-2">
                {similarUsers.map(u => (
                  <Link
                    key={u.id}
                    to={`/users/${u.id}`}
                    className="flex items-center gap-2.5 p-2.5 bg-white rounded-lg hover:shadow-sm transition-shadow"
                    data-testid={`similar-user-${u.id}`}
                  >
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={getAvatarImage(u.avatar)} />
                      <AvatarFallback className="bg-bloom-violet text-white text-xs">{u.username?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium text-bloom-text">{u.username}</p>
                      {u.location && <p className="text-xs text-bloom-sub">{u.location}</p>}
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-bloom-sub ml-auto" />
                  </Link>
                ))}
              </div>
            )}
            {similarUsers.length === 0 && saved && (
              <p className="text-xs text-bloom-sub italic">{t('cesta.nikdoNesdili')}</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
